/**
 * Тонкая обёртка над `window.Telegram.WebApp` (T11.1).
 *
 * Библиотеки нет намеренно. Бот работает с Bot API напрямую
 * (`infra/telegram/client.ts`), и здесь та же линия: чужой пакет добавил
 * бы к сборке, которую телефон грузит по мобильной сети, десятки
 * килобайт ради обёртки над полями, которых мы используем пять.
 *
 * ## Приложение обязано открываться и вне Telegram
 *
 * Не ради пользователей — их там не будет, — а ради разработки: иначе
 * ни собрать, ни посмотреть вёрстку в браузере нельзя, и каждая правка
 * требует полного круга через туннель и телефон. Поэтому отсутствие
 * `window.Telegram` — не отказ, а «мы не в Telegram»: тема тёмная,
 * `initData` пуста, вход не состоится.
 */

/** Цвета темы. Telegram присылает их шестнадцатеричными строками. */
export interface ThemeParams {
  readonly bg_color?: string;
  readonly text_color?: string;
  readonly hint_color?: string;
  readonly link_color?: string;
  readonly button_color?: string;
  readonly button_text_color?: string;
  readonly secondary_bg_color?: string;
}

/** То, чем мы пользуемся. Полный набор Telegram куда шире. */
export interface TelegramWebApp {
  readonly initData: string;
  readonly colorScheme: 'light' | 'dark';
  readonly themeParams: ThemeParams;
  ready(): void;
  expand(): void;
  onEvent(event: 'themeChanged', handler: () => void): void;
  openLink(url: string): void;
  // Появился позже прочих: старые клиенты его не знают, и вызов
  // проверяется на существование, а не предполагается
  openTelegramLink?(url: string): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

/** Приложение внутри Telegram — или `null`, если открыто в браузере. */
export function webApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/**
 * Подписанная строка входа.
 *
 * Пустая означает «мы не в Telegram». Отправлять её на сервер
 * бессмысленно — проверка подписи всё равно откажет, — но и придумывать
 * подмену нельзя: единственный способ войти задан Telegram
 * (`08_SECURITY.md` §2.1).
 */
export function initData(): string {
  return webApp()?.initData ?? '';
}

/**
 * Готовность и разворот на весь экран.
 *
 * `ready` снимает заставку: пока он не вызван, человек видит спиннер
 * Telegram поверх уже отрисованного приложения. `expand` разворачивает
 * окно — без него приложение открывается в половину экрана, и список
 * объявлений виден на треть.
 */
export function announceReady(): void {
  const app = webApp();

  if (app === null) return;

  app.ready();
  app.expand();
}

/**
 * Ведёт ли ссылка в сам Telegram.
 *
 * Сверка по имени узла, а не по вхождению строки: адрес вида
 * `example.com/t.me` ведёт куда угодно, но не в Telegram, и открывать
 * его как свой значило бы доверять чужому домену.
 */
function isTelegramLink(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();

    return host === 't.me' || host === 'telegram.me' || host === 'telegram.dog';
  } catch {
    // Неразбираемый адрес — не наш; пусть его судьбу решает обычный путь
    return false;
  }
}

/**
 * Переход на страницу площадки.
 *
 * Через Telegram, а не `window.open`: внутри Mini App новое окно
 * блокируется, и главное действие карточки — «Открыть оригинал» (L-4) —
 * молча не срабатывало бы. `openLink` открывает встроенный браузер, из
 * которого человек возвращается кнопкой «назад», а не перезапуском
 * приложения.
 *
 * Вне Telegram — обычное окно: приложение обязано открываться и в
 * браузере, иначе вёрстку не посмотреть.
 *
 * Метод появился не в первой версии Bot API, и старые клиенты его не
 * знают. Проверка на его наличие — не перестраховка: без неё карточка
 * падала бы на телефоне с неновым Telegram.
 */
export function openLink(url: string): void {
  const app = webApp();

  // Адрес самого Telegram открывается его же средством. Через
  // встроенный браузер он привёл бы человека на веб-версию Telegram —
  // то есть попросил бы войти заново ради того, чтобы написать в
  // поддержку. Проверка живёт здесь, а не у вызывающих: правило одно
  // на все ссылки, и повтори мы его в каждом месте, однажды где-нибудь
  // забыли бы
  if (app !== null && isTelegramLink(url) && typeof app.openTelegramLink === 'function') {
    app.openTelegramLink(url);

    return;
  }

  if (app !== null && typeof app.openLink === 'function') {
    app.openLink(url);

    return;
  }

  window.open(url, '_blank', 'noopener');
}

/**
 * Отправка ссылки через Telegram.
 *
 * `openTelegramLink`, а не `openLink`: адрес `t.me/share/url` открывается
 * внутри клиента выбором чата, а через встроенный браузер он привёл бы
 * человека на веб-версию Telegram — то есть попросил бы войти заново
 * ради того, чтобы переслать сообщение.
 *
 * Запасной путь тот же, что у прочих ссылок: вне Telegram и на старых
 * клиентах открывается обычное окно. Поделиться там сложнее, но
 * невозможным это не становится.
 */
export function shareLink(url: string, text: string): void {
  const share = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  const app = webApp();

  if (app !== null && typeof app.openTelegramLink === 'function') {
    app.openTelegramLink(share);

    return;
  }

  openLink(share);
}
